/**
 * EditNumberSeriesModal Component
 *
 * Edits one agenda's number series, previewing the number it would issue while typing.
 */
import { useRef } from 'react';
import { FormModal, Input, InputWrapper } from '@insio/ui-toolkit';
import {
  NUMBER_SERIES_NUMBER_MAX_LENGTH,
  NUMBER_SERIES_PADDING_MAX,
  NUMBER_SERIES_PADDING_MIN,
  NUMBER_SERIES_PREFIX_MAX_LENGTH,
  NUMBER_SERIES_PATTERN_MAX_LENGTH,
  NumberSeriesPatternIssueCode,
  type NumberSeriesPatternIssue,
  type NumberSeriesPlaceholder
} from '@insio/shared-types';
import type { NumberSeriesFieldsFragment } from '@/graphql';
import { useEnumTranslation } from '@/hooks/useEnumTranslation';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';
import type { NumberSeriesDraft } from '../../../hooks/use-number-series-settings';
import { NumberSeriesPlaceholderHints } from './NumberSeriesPlaceholderHints';

/** Which input an issue is about, so it is reported next to the value that caused it. */
type IssueField = 'prefix' | 'pattern' | 'padding' | 'number';

const ISSUE_FIELD: Record<NumberSeriesPatternIssueCode, IssueField> = {
  EMPTY: 'pattern',
  TOO_LONG: 'pattern',
  UNKNOWN_PLACEHOLDER: 'pattern',
  UNBALANCED_BRACE: 'pattern',
  MISSING_SEQUENCE: 'pattern',
  PREFIX_EMPTY: 'prefix',
  PREFIX_TOO_LONG: 'prefix',
  PADDING_OUT_OF_RANGE: 'padding',
  RENDERED_TOO_LONG: 'number'
};

interface EditNumberSeriesModalProps {
  series: NumberSeriesFieldsFragment;
  draft: NumberSeriesDraft;
  issues: readonly NumberSeriesPatternIssue[];
  preview: string;
  canSave: boolean;
  isSaving: boolean;
  error: string | null;
  onChange: (patch: Partial<NumberSeriesDraft>) => void;
  onSubmit: () => void | Promise<void>;
  onClose: () => void;
}

export function EditNumberSeriesModal({
  series,
  draft,
  issues,
  preview,
  canSave,
  isSaving,
  error,
  onChange,
  onSubmit,
  onClose
}: EditNumberSeriesModalProps) {
  const t = useFeatureTranslation('systemSettings.numberSeries');
  const tDocumentType = useEnumTranslation('numberSeriesDocumentType');
  const patternInputRef = useRef<HTMLInputElement>(null);
  // Where the caret was when the pattern field last lost focus — clicking a placeholder
  // button blurs it, so the live selection is gone by the time the click handler runs.
  const patternCaretRef = useRef<{ start: number; end: number } | null>(null);

  /** Inserts a placeholder at the last caret position, appending when there was none. */
  const insertPlaceholder = (placeholder: NumberSeriesPlaceholder) => {
    const token = `{${placeholder}}`;
    const { start, end } = patternCaretRef.current ?? {
      start: draft.pattern.length,
      end: draft.pattern.length
    };
    const pattern = draft.pattern.slice(0, start) + token + draft.pattern.slice(end);
    if (pattern.length > NUMBER_SERIES_PATTERN_MAX_LENGTH) return;

    const caret = start + token.length;
    patternCaretRef.current = { start: caret, end: caret };
    onChange({ pattern });
    // The new value only lands on the next render, so move the caret after it.
    requestAnimationFrame(() => {
      patternInputRef.current?.focus();
      patternInputRef.current?.setSelectionRange(caret, caret);
    });
  };

  const issuesOf = (field: IssueField) => issues.filter(issue => ISSUE_FIELD[issue.code] === field);
  const prefixIssues = issuesOf('prefix');
  const patternIssues = issuesOf('pattern');
  const paddingIssues = issuesOf('padding');
  const numberIssues = issuesOf('number');

  const issueList = (field: IssueField, fieldIssues: readonly NumberSeriesPatternIssue[]) =>
    fieldIssues.length > 0 && (
      <ul className='mt-xs space-y-2xs' data-testid={`number-series-${field}-issues`}>
        {fieldIssues.map(issue => (
          <li key={`${issue.code}-${issue.placeholder ?? ''}`} className='text-sm text-error-text'>
            {t(`issues.${issue.code}`, {
              placeholder: issue.placeholder ?? '',
              max: NUMBER_SERIES_PATTERN_MAX_LENGTH,
              prefixMax: NUMBER_SERIES_PREFIX_MAX_LENGTH,
              paddingMin: NUMBER_SERIES_PADDING_MIN,
              paddingMax: NUMBER_SERIES_PADDING_MAX,
              numberMax: NUMBER_SERIES_NUMBER_MAX_LENGTH
            })}
          </li>
        ))}
      </ul>
    );

  return (
    <FormModal
      open
      testId='number-series-edit-modal'
      title={t('modal.title', { documentType: tDocumentType(series.documentType) })}
      description={t('modal.description')}
      submitLabel={t('actions.save')}
      cancelLabel={t('actions.cancel')}
      loading={isSaving}
      submitDisabled={!canSave}
      error={error}
      onSubmit={onSubmit}
      onClose={onClose}
    >
      <div className='space-y-md'>
        <InputWrapper
          label={t('fields.prefix')}
          htmlFor='number-series-prefix'
          labelSize='sm'
          isError={prefixIssues.length > 0}
        >
          <Input
            id='number-series-prefix'
            value={draft.prefix}
            maxLength={NUMBER_SERIES_PREFIX_MAX_LENGTH}
            autoComplete='off'
            hasError={prefixIssues.length > 0}
            onChange={event => onChange({ prefix: event.target.value })}
          />
          {issueList('prefix', prefixIssues)}
        </InputWrapper>

        <InputWrapper
          label={t('fields.pattern')}
          htmlFor='number-series-pattern'
          labelSize='sm'
          isError={patternIssues.length > 0}
        >
          <Input
            id='number-series-pattern'
            ref={patternInputRef}
            value={draft.pattern}
            maxLength={NUMBER_SERIES_PATTERN_MAX_LENGTH}
            autoComplete='off'
            hasError={patternIssues.length > 0}
            onChange={event => onChange({ pattern: event.target.value })}
            onBlur={event => {
              patternCaretRef.current = {
                start: event.target.selectionStart ?? event.target.value.length,
                end: event.target.selectionEnd ?? event.target.value.length
              };
            }}
          />
          {issueList('pattern', patternIssues)}
        </InputWrapper>

        <InputWrapper
          label={t('fields.padding')}
          htmlFor='number-series-padding'
          labelSize='sm'
          isError={paddingIssues.length > 0}
        >
          <Input
            id='number-series-padding'
            type='number'
            inputMode='numeric'
            min={NUMBER_SERIES_PADDING_MIN}
            max={NUMBER_SERIES_PADDING_MAX}
            value={draft.padding}
            hasError={paddingIssues.length > 0}
            onChange={event => onChange({ padding: event.target.value })}
          />
          {issueList('padding', paddingIssues)}
        </InputWrapper>

        <div className='rounded-lg bg-background-muted p-md'>
          <p className='text-sm font-medium text-foreground-base'>{t('fields.preview')}</p>
          <code
            className='mt-xs block text-sm font-mono text-foreground-base'
            data-testid='number-series-live-preview'
          >
            {preview}
          </code>
          {issueList('number', numberIssues)}
        </div>

        <NumberSeriesPlaceholderHints onInsert={insertPlaceholder} />
      </div>
    </FormModal>
  );
}
